export const RESOURCE_CONFIG_DEFAULTS = {
  postgresql: {
    host: '',
    port: 5432,
    database: '',
    username: '',
    password: '',
    ssl_mode: 'disable',
    max_connections: '',
    connect_timeout: '',
    schema: ''
  },
  redis: {
    host: '',
    port: 6379,
    password: '',
    database: 0,
    max_connections: '',
    connect_timeout: '',
    tls: false,
    cluster_mode: false,
    cluster_nodes: ''
  },
  rest_api: {
    base_url: '',
    auth_type: '',
    auth_token: '',
    username: '',
    password: '',
    api_key: '',
    api_key_header: '',
    timeout: 30,
    rate_limit: '',
    max_retries: '',
    retry_delay: '',
    headers_json: '{}'
  },
  graphql: {
    endpoint: '',
    auth_type: '',
    auth_token: '',
    api_key: '',
    api_key_header: '',
    timeout: 30,
    rate_limit: '',
    max_retries: '',
    headers_json: '{}'
  },
  gcs: {
    project_id: '',
    bucket_name: '',
    service_account_json: '',
    use_default_creds: false,
    storage_emulator_host: ''
  }
}

export function getDefaultResourceConfig(type) {
  return { ...(RESOURCE_CONFIG_DEFAULTS[type] || {}) }
}

export function validateResourceConfig(type, config) {
  const errors = {}

  if (type === 'postgresql') {
    if (!config.host?.trim()) errors.host = 'Host is required'
    if (!config.database?.trim()) errors.database = 'Database is required'
    if (!config.username?.trim()) errors.username = 'Username is required'
  }

  if (type === 'redis') {
    if (!config.host?.trim()) errors.host = 'Host is required'
  }

  if (type === 'rest_api') {
    if (!config.base_url?.trim()) errors.base_url = 'Base URL is required'
    validateHeadersJSON(config.headers_json, errors)
  }

  if (type === 'graphql') {
    if (!config.endpoint?.trim()) errors.endpoint = 'Endpoint is required'
    validateHeadersJSON(config.headers_json, errors)
  }

  if (type === 'gcs') {
    if (!config.bucket_name?.trim()) errors.bucket_name = 'Bucket name is required'
  }

  if (!RESOURCE_CONFIG_DEFAULTS[type]) {
    errors.type = 'Resource type is not supported'
  }

  return errors
}

export function normalizeResourceConfig(type, config) {
  switch (type) {
    case 'postgresql':
      return withoutEmptyValues({
        host: cleanString(config.host),
        port: parseInteger(config.port, 5432),
        database: cleanString(config.database),
        username: cleanString(config.username),
        password: config.password || '',
        ssl_mode: config.ssl_mode || 'disable',
        max_connections: parseOptionalInteger(config.max_connections),
        connect_timeout: parseOptionalInteger(config.connect_timeout),
        schema: cleanString(config.schema)
      })

    case 'redis':
      return withoutEmptyValues({
        host: cleanString(config.host),
        port: parseInteger(config.port, 6379),
        password: config.password || '',
        database: parseInteger(config.database, 0),
        max_connections: parseOptionalInteger(config.max_connections),
        connect_timeout: parseOptionalInteger(config.connect_timeout),
        tls: Boolean(config.tls),
        cluster_mode: Boolean(config.cluster_mode),
        cluster_nodes: parseClusterNodes(config.cluster_nodes)
      })

    case 'rest_api':
      return withoutEmptyValues({
        base_url: cleanString(config.base_url),
        headers: parseHeaders(config.headers_json),
        auth_type: cleanString(config.auth_type),
        auth_token: config.auth_token || '',
        username: cleanString(config.username),
        password: config.password || '',
        api_key: config.api_key || '',
        api_key_header: cleanString(config.api_key_header),
        timeout: parseOptionalInteger(config.timeout),
        rate_limit: parseOptionalInteger(config.rate_limit),
        max_retries: parseOptionalInteger(config.max_retries),
        retry_delay: parseOptionalInteger(config.retry_delay)
      })

    case 'graphql':
      return withoutEmptyValues({
        endpoint: cleanString(config.endpoint),
        headers: parseHeaders(config.headers_json),
        auth_type: cleanString(config.auth_type),
        auth_token: config.auth_token || '',
        api_key: config.api_key || '',
        api_key_header: cleanString(config.api_key_header),
        timeout: parseOptionalInteger(config.timeout),
        rate_limit: parseOptionalInteger(config.rate_limit),
        max_retries: parseOptionalInteger(config.max_retries)
      })

    case 'gcs':
      return withoutEmptyValues({
        project_id: cleanString(config.project_id),
        bucket_name: cleanString(config.bucket_name),
        service_account_json: config.service_account_json || '',
        use_default_creds: Boolean(config.use_default_creds),
        storage_emulator_host: cleanString(config.storage_emulator_host)
      })

    default:
      return {}
  }
}

export function parseRawResourceConfig(rawValue) {
  try {
    const parsed = JSON.parse(rawValue)
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return { config: null, error: 'JSON must be an object' }
    }
    return { config: parsed, error: null }
  } catch {
    return { config: null, error: 'JSON is invalid' }
  }
}

function validateHeadersJSON(rawValue, errors) {
  if (!rawValue?.trim()) return
  const parsed = parseRawResourceConfig(rawValue)
  if (parsed.error) {
    errors.headers_json = parsed.error
  }
}

function parseHeaders(rawValue) {
  if (!rawValue?.trim()) return {}
  const parsed = parseRawResourceConfig(rawValue)
  return parsed.config || {}
}

function parseClusterNodes(value) {
  if (!value?.trim()) return undefined
  return value
    .split('\n')
    .map((node) => node.trim())
    .filter(Boolean)
}

function parseInteger(value, fallback) {
  if (value === '' || value === null || value === undefined) return fallback
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? fallback : parsed
}

function parseOptionalInteger(value) {
  if (value === '' || value === null || value === undefined) return undefined
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function withoutEmptyValues(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => {
      if (entry === undefined || entry === null) return false
      if (typeof entry === 'string' && entry === '') return false
      if (Array.isArray(entry) && entry.length === 0) return false
      return true
    })
  )
}
